import * as R from 'ramda';
import pathIsFalsy from '../pathIsFalsy';
import pathIsTruthy from '../pathisTruthy';
import getFeatureFlagStatus from 'App/utils/getFeatureFlagStatus';

const isFeatureFlagEnabled = R.thunkify(getFeatureFlagStatus);

const hasClinicalAdmin = pathIsTruthy(['company_permissions', 'TherigyAssessmentAdmin']);
const isReportBuilder = pathIsTruthy(['active_branch', 'Privileges', 'ReportBuilder']);
const isReportEngineEnabled = pathIsTruthy(['company_permissions', 'ReportEngine']);
const isInsightsEnabled = R.allPass([
    isReportEngineEnabled,
    R.anyPass([pathIsTruthy(['active_branch', 'Privileges', 'ReportViewer']), isReportBuilder]),
]);
const isSsoEnabled = pathIsTruthy(['company_permissions', 'SsoEnabled']);
const isPatientMessagingEnabled = pathIsTruthy(['company_permissions', 'PatientMessaging']);
const isMassTextingEnabled = R.allPass([
    pathIsTruthy(['company_permissions', 'SmsMassTexting']),
    isPatientMessagingEnabled,
]);
const isCompanyAdmin = pathIsTruthy(['active_branch', 'Privileges', 'CompanyAdmin']);
const isBranchAdmin = pathIsTruthy(['active_branch', 'Privileges', 'BranchAdmin']);
const isPDFGenerationEnabled = pathIsTruthy(['company_permissions', 'Pdf']);
const canAddPatient = R.allPass([
    pathIsTruthy(['active_branch', 'Privileges', 'AddPatient']),
    pathIsFalsy(['company_permissions', 'DisableAddPatient']),
    pathIsFalsy(['company_permissions', 'Pdx']),
]);
const hasMessagingCapability = R.anyPass([
    pathIsTruthy(['company_permissions', 'SmsMassTexting']),
    pathIsTruthy(['company_permissions', 'SmsActivityCustomSchedule']),
    pathIsTruthy(['company_permissions', 'GenerateMobileLink']),
]);

const isReferralActivityTriggers = isFeatureFlagEnabled('FEATURE_REFERRAL_ACTIVITY_TRIGGERS');
const isTreatmentHistoryEnabled = isFeatureFlagEnabled('ENABLE_TREATMENT_HISTORY');
const isWorkQueueFiltersEnabled = isFeatureFlagEnabled('ENABLE_WORK_QUEUE_FILTERS');
const isPdxServerlessEndpoint = isFeatureFlagEnabled('ENABLE_PDX_SLS');
const isCodifiedMedicationsEnabled = isFeatureFlagEnabled('ENABLE_CODIFY_MEDICATIONS');
const isWorkQueueIsNotFilterEnabled = isFeatureFlagEnabled('ENABLE_WORK_QUEUE_IS_NOT');
const isSuperCloneAssessmentEnabled = isFeatureFlagEnabled('ENABLE_SUPER_CLONE_ASSESSMENT');
const isWorkQueueLabelsFilterEnabled = isFeatureFlagEnabled('ENABLE_REACT_WORK_QUEUE_LABELS');
const isPatientDischargeEnabled = isFeatureFlagEnabled('ENABLE_FEATURE_PATIENT_DISCHARGE');
const isAddEditPatientUIEnabled = isFeatureFlagEnabled('ENABLE_ADD_EDIT_PATIENT_UI');
const isSlsRmsEnabled = isFeatureFlagEnabled('ENABLE_SLS_RMS');

const everyone = R.T;

/**
 * The function mapping for each permission type.
 */
const permissionFns = Object.freeze({
    ADD_PATIENT: canAddPatient,

    ADVANCED_REFERRAL: pathIsTruthy(['company_permissions', 'AdvancedReferral']),

    ADVANCED_REPORTS: pathIsTruthy(['company_permissions', 'AdvanceReport']),

    ALERT_CENTER: pathIsTruthy(['company_permissions', 'AdvancedReferral']),

    ALERT_MANAGER: R.allPass([
        pathIsTruthy(['company_permissions', 'AdvancedReferral']),
        pathIsTruthy(['active_branch', 'Privileges', 'CompanyAdmin']),
    ]),

    ASSESSMENT_MANAGER: pathIsTruthy(['company_permissions', 'AssessmentManager']),

    ASSESSMENT_PUBLISHER: pathIsTruthy(['company_permissions', 'TherigyAssessmentAdmin']),

    ASSIGNMENT_MANAGER: R.anyPass([
        isCompanyAdmin,
        isBranchAdmin,
        pathIsTruthy(['active_branch', 'Privileges', 'WorkQueueAssignment']),
    ]),

    BRANCH_ADMIN: isBranchAdmin,

    CLINICAL_ADMIN: pathIsTruthy(['company_permissions', 'TherigyAssessmentAdmin']),

    CLONE_ASSESSMENT: pathIsTruthy(['company_permissions', 'TherigyAssessmentAdmin']),

    CONTENT_LIBRARY: pathIsTruthy(['company_permissions', 'TherigyAssessmentAdmin']),

    CONTENT_PUBLISHER: pathIsTruthy(['company_permissions', 'TherigyAssessmentAdmin']),

    COMPANY_ADMIN: isCompanyAdmin,

    PDF_GENERATION: isPDFGenerationEnabled,

    DELETE_THERIGY_PROTOCOL: R.allPass([hasClinicalAdmin]),

    EDIT_PROTOCOLS: pathIsTruthy(['user_permissions', 'EditProtocols']),

    ENABLE_ADD_EDIT_PATIENT_UI: isAddEditPatientUIEnabled,

    SSO_CONFIGURATION: R.allPass([isCompanyAdmin, isSsoEnabled]),

    ENABLE_SUPER_CLONE_ASSESSMENT: isSuperCloneAssessmentEnabled,

    SSO_ENABLED: isSsoEnabled,

    ENABLE_TREATMENT_HISTORY: isTreatmentHistoryEnabled,

    ENABLE_REFERRAL: R.allPass([
        pathIsTruthy(['company_permissions', 'AdvancedReferral']),
        pathIsTruthy(['company_permissions', 'ReferralManager']),
    ]),

    EVERYONE: everyone,

    FEATURE_REFERRAL_ACTIVITY_TRIGGERS: isReferralActivityTriggers,

    FIND_PATIENT: pathIsFalsy(['company_permissions', 'Pdx']),

    MEDICATION_MANAGEMENT: isCompanyAdmin,

    PATIENT_PREDICTIVE_MODEL: pathIsTruthy(['company_permissions', 'PatientPredictiveModel']),

    PDX_FIND_PATIENT: pathIsTruthy(['company_permissions', 'Pdx']),

    PROTOCOL_MANAGER: pathIsTruthy(['company_permissions', 'ClientProtocolBuilder']),

    PUBLISH_PROTOCOL: R.allPass([hasClinicalAdmin]),

    QUESTION_GROUPS: R.allPass([hasClinicalAdmin]),

    QUEUE_MANAGER: isCompanyAdmin,

    SHOW_RETIRED_ASSESSMENTS: pathIsTruthy(['company_permissions', 'ShowRetiredAssessments']),

    THERAPEUTIC_CATEGORIES: R.allPass([hasClinicalAdmin]),

    REPORT_BUILDER: R.allPass([isReportEngineEnabled, isReportBuilder]),

    REPORT_ENGINE: isReportEngineEnabled,

    THERIGY_INSIGHTS: isInsightsEnabled,

    TMS_ENABLED: pathIsTruthy(['company_permissions', 'TmsEndpoint']),

    UPLOAD_PATIENTS: canAddPatient,

    UPLOAD_PHYSICIANS: everyone,

    USER_MANAGER: R.anyPass([isCompanyAdmin, isBranchAdmin]),

    ENABLE_WORK_QUEUE_FILTERS: isWorkQueueFiltersEnabled,

    ENABLE_WORK_QUEUE_IS_NOT: isWorkQueueIsNotFilterEnabled,

    ENABLE_REACT_WORK_QUEUE_LABELS: isWorkQueueLabelsFilterEnabled,

    ENABLE_SLS_RMS: isSlsRmsEnabled,

    WORK_QUEUE: everyone,

    PATIENT_MESSAGING: R.allPass([
        isCompanyAdmin,
        R.anyPass([isPatientMessagingEnabled, pathIsTruthy(['company_permissions', 'GenerateMobileLink'])]),
        hasMessagingCapability,
    ]),

    LINK_2_TEXT: R.allPass([isCompanyAdmin, pathIsTruthy(['company_permissions', 'GenerateMobileLink'])]),

    MASS_TEXTING: isMassTextingEnabled,

    ACTIVITY_TEXT_SCHEDULE: pathIsTruthy(['company_permissions', 'SmsActivityCustomSchedule']),

    MCKESSON_NODE_RED: pathIsTruthy(['company_permissions', 'MckessonNodeRed']),

    ACTIVE_PCC_ONLY: R.allPass([
        pathIsFalsy(['active_roles', 'RN']),
        pathIsFalsy(['active_roles', 'RPh']),
        pathIsTruthy(['active_roles', 'PCC']),
    ]),

    ENABLE_PDX_SLS: R.allPass([pathIsTruthy(['company_permissions', 'Pdx']), isPdxServerlessEndpoint]),

    ENABLE_CODIFY_MEDICATIONS: R.allPass([hasClinicalAdmin, isCodifiedMedicationsEnabled]),

    ENABLE_FEATURE_PATIENT_DISCHARGE: isPatientDischargeEnabled,

    ENABLE_CODIFY_ADD_EDIT_MEDICATIONS: R.F,

    GENERATE_MOBILE_LINK: pathIsTruthy(['company_permissions', 'GenerateMobileLink']),

    SMS_SEND_LINK: R.allPass([pathIsTruthy(['company_permissions', 'SmsSendLink']), isPatientMessagingEnabled]),

    PATIENT_MESSAGING_CONF: isPatientMessagingEnabled,
});

/**
 * An "enum" of permission types.
 * ** Please add to this definition any time you add a new type to the collection above.**
 * @typedef PERMISSION_TYPES
 * @type {object}
 * @property {string} ACTIVE_PCC_ONLY
 * @property {string} ACTIVITY_TEXT_SCHEDULE
 * @property {string} ADD_PATIENT
 * @property {string} ADVANCED_REFERRAL
 * @property {string} ADVANCED_REPORTS
 * @property {string} ALERT_CENTER
 * @property {string} ALERT_MANAGER
 * @property {string} ASSESSMENT_MANAGER
 * @property {string} ASSESSMENT_PUBLISHER
 * @property {string} ASSIGNMENT_MANAGER
 * @property {string} BRANCH_ADMIN
 * @property {string} CLINICAL_ADMIN
 * @property {string} CLONE_ASSESSMENT
 * @property {string} COMPANY_ADMIN
 * @property {string} CONTENT_LIBRARY
 * @property {string} CONTENT_PUBLISHER
 * @property {string} DELETE_THERIGY_PROTOCOL
 * @property {string} EDIT_PROTOCOLS
 * @property {string} ENABLE_ADD_EDIT_PATIENT_UI
 * @property {string} ENABLE_CODIFY_ADD_EDIT_MEDICATIONS
 * @property {string} ENABLE_CODIFY_MEDICATIONS
 * @property {string} ENABLE_FEATURE_PATIENT_DISCHARGE
 * @property {string} ENABLE_PDX_SLS
 * @property {string} ENABLE_REACT_WORK_QUEUE_LABELS
 * @property {string} ENABLE_REFERRAL
 * @property {string} ENABLE_SLS_RMS
 * @property {string} SSO_CONFIGURATION
 * @property {string} ENABLE_SUPER_CLONE_ASSESSMENT
 * @property {string} ENABLE_TREATMENT_HISTORY
 * @property {string} ENABLE_WORK_QUEUE_FILTERS
 * @property {string} ENABLE_WORK_QUEUE_IS_NOT
 * @property {string} EVERYONE
 * @property {string} FEATURE_REFERRAL_ACTIVITY_TRIGGERS
 * @property {string} FIND_PATIENT
 * @property {string} GENERATE_MOBILE_LINK
 * @property {string} LINK_2_TEXT
 * @property {string} MASS_TEXTING
 * @property {string} MCKESSON_NODE_RED
 * @property {string} MEDICATION_MANAGEMENT
 * @property {string} PATIENT_MESSAGING
 * @property {string} PATIENT_MESSAGING_CONF
 * @property {string} PATIENT_PREDICTIVE_MODEL
 * @property {string} PDF_GENERATION
 * @property {string} PDX_FIND_PATIENT
 * @property {string} PROTOCOL_MANAGER
 * @property {string} PUBLISH_PROTOCOL
 * @property {string} QUESTION_GROUPS
 * @property {string} QUEUE_MANAGER
 * @property {string} REPORT_BUILDER
 * @property {string} REPORT_ENGINE
 * @property {string} SHOW_RETIRED_ASSESSMENTS
 * @property {string} SMS_SEND_LINK
 * @property {string} SSO_ENABLED
 * @property {string} THERAPEUTIC_CATEGORIES
 * @property {string} THERIGY_INSIGHTS
 * @property {string} TMS_ENABLED
 * @property {string} UPLOAD_PATIENTS
 * @property {string} UPLOAD_PHYSICIANS
 * @property {string} USER_MANAGER
 * @property {string} WORK_QUEUE
 */

/**
 * An "enum" of permission types.
 * @type PERMISSION_TYPES
 */
const PERMISSION_TYPES = R.mapObjIndexed((value, key) => key, permissionFns);

/**
 * Determines if the user has a given permission type.
 * Curried
 * @param {string} permissionType - The permission type
 * @param {object} user - The user object which contains the permission properties. The token.
 */
const isPermitted = R.curry((permissionType, user) => {
    const fn = permissionFns[permissionType];

    if (typeof fn === 'function') {
        return fn(user);
    }
    return false;
});

export { PERMISSION_TYPES };

export default isPermitted;
